import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Pen, Square, Type, Undo2, Trash2, Check, X } from 'lucide-react';

type Tool = 'pen' | 'rect' | 'text';
const COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#f59e0b', '#000000', '#ffffff'];

interface Props {
    image: string;
    onSave: (annotated: string) => void;
    onCancel: () => void;
}

interface DrawAction {
    tool: Tool;
    color: string;
    points?: { x: number; y: number }[];
    rect?: { x: number; y: number; w: number; h: number };
    text?: { x: number; y: number; content: string };
}

export function ScreenshotAnnotator({ image, onSave, onCancel }: Props) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [tool, setTool] = useState<Tool>('pen');
    const [color, setColor] = useState('#ef4444');
    const [actions, setActions] = useState<DrawAction[]>([]);
    const [drawing, setDrawing] = useState(false);
    const [currentAction, setCurrentAction] = useState<DrawAction | null>(null);
    const [imgObj, setImgObj] = useState<HTMLImageElement | null>(null);
    const [textInput, setTextInput] = useState<{ x: number; y: number } | null>(null);
    const [textValue, setTextValue] = useState('');
    const [canvasSize, setCanvasSize] = useState({ w: 800, h: 500 });

    useEffect(() => {
        const img = new Image();
        img.onload = () => {
            setImgObj(img);
            // Constrain display size nicely within modal viewport
            const maxW = Math.min(img.width, window.innerWidth - 120);
            const scale = maxW / img.width;
            setCanvasSize({ w: maxW, h: img.height * scale });
        };
        img.src = image;
    }, [image]);

    const redraw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !imgObj) return;
        const ctx = canvas.getContext('2d')!;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(imgObj, 0, 0, canvas.width, canvas.height);

        const drawAction = (a: DrawAction) => {
            ctx.strokeStyle = a.color;
            ctx.fillStyle = a.color;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.globalAlpha = 1;

            if (a.tool === 'pen' && a.points && a.points.length > 1) {
                ctx.lineWidth = 3;
                ctx.beginPath();
                ctx.moveTo(a.points[0].x, a.points[0].y);
                a.points.forEach(p => ctx.lineTo(p.x, p.y));
                ctx.stroke();
            }
            if (a.tool === 'rect' && a.rect) {
                ctx.lineWidth = 2.5;
                ctx.strokeRect(a.rect.x, a.rect.y, a.rect.w, a.rect.h);
            }
            if (a.tool === 'text' && a.text) {
                ctx.font = 'bold 16px Inter, system-ui, sans-serif';
                // Small white outline for contrast
                ctx.strokeStyle = '#ffffff';
                ctx.lineWidth = 3.5;
                ctx.strokeText(a.text.content, a.text.x, a.text.y);
                // Fill text
                ctx.fillStyle = a.color;
                ctx.fillText(a.text.content, a.text.x, a.text.y);
            }
        };

        actions.forEach(drawAction);
        if (currentAction) drawAction(currentAction);
    }, [imgObj, actions, currentAction]);

    useEffect(() => { redraw(); }, [redraw]);

    const getPos = (e: React.MouseEvent) => {
        const r = canvasRef.current!.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    const onMouseDown = (e: React.MouseEvent) => {
        if (tool === 'text') {
            setTextInput(getPos(e));
            setTextValue('');
            return;
        }
        setDrawing(true);
        const pos = getPos(e);
        if (tool === 'pen') {
            setCurrentAction({ tool: 'pen', color, points: [pos] });
        } else if (tool === 'rect') {
            setCurrentAction({ tool: 'rect', color, rect: { x: pos.x, y: pos.y, w: 0, h: 0 } });
        }
    };

    const onMouseMove = (e: React.MouseEvent) => {
        if (!drawing || !currentAction) return;
        const pos = getPos(e);
        if (currentAction.tool === 'pen') {
            setCurrentAction({
                ...currentAction,
                points: [...(currentAction.points || []), pos]
            });
        } else if (currentAction.tool === 'rect' && currentAction.rect) {
            setCurrentAction({
                ...currentAction,
                rect: {
                    x: Math.min(currentAction.rect.x, pos.x),
                    y: Math.min(currentAction.rect.y, pos.y),
                    w: Math.abs(pos.x - currentAction.rect.x),
                    h: Math.abs(pos.y - currentAction.rect.y),
                }
            });
        }
    };

    const onMouseUp = () => {
        if (!drawing || !currentAction) return;
        setDrawing(false);
        setActions(prev => [...prev, currentAction]);
        setCurrentAction(null);
    };

    const handleTextSubmit = () => {
        if (textInput && textValue.trim()) {
            setActions(prev => [
                ...prev,
                { tool: 'text', color, text: { x: textInput.x, y: textInput.y, content: textValue.trim() } }
            ]);
        }
        setTextInput(null);
        setTextValue('');
    };

    const handleSave = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        onSave(canvas.toDataURL('image/png'));
    };

    return (
        <div className="fixed inset-0 z-[110] bg-slate-950/80 backdrop-blur-sm flex flex-col items-center justify-center p-4">
            {/* Toolbar */}
            <div className="bg-slate-900 text-white rounded-xl shadow-2xl border border-slate-700 p-2.5 mb-3 flex items-center gap-3">
                {/* Tools */}
                <div className="flex items-center gap-1 bg-slate-800 p-1 rounded-lg">
                    <button
                        type="button"
                        onClick={() => setTool('pen')}
                        className={`p-1.5 rounded transition-all ${tool === 'pen' ? 'bg-sky-600 text-white' : 'text-slate-400 hover:text-white'}`}
                        title="Bút vẽ"
                    >
                        <Pen size={16} />
                    </button>
                    <button
                        type="button"
                        onClick={() => setTool('rect')}
                        className={`p-1.5 rounded transition-all ${tool === 'rect' ? 'bg-sky-600 text-white' : 'text-slate-400 hover:text-white'}`}
                        title="Khoanh vùng"
                    >
                        <Square size={16} />
                    </button>
                    <button
                        type="button"
                        onClick={() => setTool('text')}
                        className={`p-1.5 rounded transition-all ${tool === 'text' ? 'bg-sky-600 text-white' : 'text-slate-400 hover:text-white'}`}
                        title="Viết chữ"
                    >
                        <Type size={16} />
                    </button>
                </div>

                <div className="w-px h-6 bg-slate-700" />

                {/* Colors */}
                <div className="flex items-center gap-1.5">
                    {COLORS.map(c => (
                        <button
                            key={c}
                            type="button"
                            onClick={() => setColor(c)}
                            className={`w-5 h-5 rounded-full border-2 transition-transform ${color === c ? 'border-white scale-125' : 'border-slate-600 hover:scale-110'}`}
                            style={{ backgroundColor: c }}
                        />
                    ))}
                </div>

                <div className="w-px h-6 bg-slate-700" />

                {/* Actions */}
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => setActions(prev => prev.slice(0, -1))}
                        disabled={actions.length === 0}
                        className="p-1.5 rounded text-slate-300 hover:text-white hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-transparent"
                        title="Hoàn tác"
                    >
                        <Undo2 size={16} />
                    </button>
                    <button
                        type="button"
                        onClick={() => setActions([])}
                        disabled={actions.length === 0}
                        className="p-1.5 rounded text-rose-400 hover:text-rose-300 hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-transparent"
                        title="Xóa hết"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>

                <div className="w-px h-6 bg-slate-700" />

                {/* Action buttons */}
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-3 py-1 text-xs text-slate-300 hover:text-white rounded-lg transition-colors cursor-pointer"
                >
                    ✕ Hủy
                </button>
                <button
                    type="button"
                    onClick={handleSave}
                    className="px-3.5 py-1 text-xs font-semibold bg-sky-600 hover:bg-sky-500 text-white rounded-lg shadow transition-all flex items-center gap-1 cursor-pointer"
                >
                    <Check size={14} /> Xong
                </button>
            </div>

            {/* Canvas Container */}
            <div className="relative rounded-lg shadow-2xl overflow-hidden border border-slate-700 bg-slate-900">
                <canvas
                    ref={canvasRef}
                    width={canvasSize.w}
                    height={canvasSize.h}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                    className="block cursor-crosshair max-h-[calc(100vh-140px)] object-contain"
                />

                {/* Inline Text Box */}
                {textInput && (
                    <div
                        className="absolute z-20 flex items-center gap-1 p-1 bg-slate-900/90 backdrop-blur rounded border border-sky-400 shadow-xl"
                        style={{ left: textInput.x, top: textInput.y }}
                    >
                        <input
                            autoFocus
                            value={textValue}
                            onChange={e => setTextValue(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') handleTextSubmit();
                                if (e.key === 'Escape') setTextInput(null);
                            }}
                            placeholder="Gõ ghi chú..."
                            className="bg-transparent text-sm text-white px-1.5 py-0.5 outline-none w-44"
                            style={{ color }}
                        />
                        <button
                            type="button"
                            onClick={handleTextSubmit}
                            className="px-2 py-0.5 bg-sky-600 text-white text-xs font-medium rounded hover:bg-sky-500"
                        >
                            OK
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ScreenshotAnnotator;
